import React, { useEffect, useState } from "react"

import { trpc } from "../lib/trpc"

// Hardcoded integrations data
const AVAILABLE_INTEGRATIONS = [
  {
    id: "geekbot",
    name: "Geekbot",
    description: "Async standup reports and team insights",
    type: "standup",
    icon: "🤖",
    isConnected: true, // Always connected since we have hardcoded API key
    provider: "Geekbot",
    category: "Team Management",
    features: ["Daily standup reports", "Team engagement metrics", "Custom workflows", "Slack/Teams integration"],
    documentation: "https://geekbot.com/developers/",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Team communication and collaboration",
    type: "communication",
    icon: "💬",
    isConnected: false, // Will be dynamically set based on OAuth
    provider: "Slack",
    category: "Communication",
    features: [
      "Send notifications to channels",
      "Direct message users",
      "Read channel messages",
      "File uploads",
      "Emoji reactions",
      "Bulk notifications",
    ],
    documentation: "https://api.slack.com/",
  },
  {
    id: "linear",
    name: "Linear",
    description: "Issue tracking and team management",
    type: "project_management",
    icon: "📋",
    isConnected: true, // Always connected since we have hardcoded API key
    provider: "Linear",
    category: "Development",
    features: [
      "View team members",
      "Browse teams and workspaces",
      "Track team issues and tasks",
      "Team issue tracking",
      "Team workspaces",
      "Issue prioritization",
    ],
    documentation: "https://developers.linear.app/",
  },
  {
    id: "github",
    name: "GitHub",
    description: "Code repository and project management",
    type: "development",
    icon: "🐙",
    isConnected: false,
    provider: "GitHub",
    category: "Development",
    features: ["Repository insights", "Issue tracking", "Pull request management", "Commit history"],
    documentation: "https://docs.github.com/en/rest",
  },
]

// Local storage key for Slack access token
const SLACK_TOKEN_KEY = "slack_access_token"
const SLACK_TEAM_KEY = "slack_team_info"

const IntegrationsScreen: React.FC = () => {
  const [selectedIntegration, setSelectedIntegration] = useState<string>("geekbot")
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [isLoading, setIsLoading] = useState(false)
  const [slackAccessToken, setSlackAccessToken] = useState<string | null>(null)
  const [slackTeamInfo, setSlackTeamInfo] = useState<any>(null)
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)

  // Check for Slack access token in localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(SLACK_TOKEN_KEY)
    const teamInfo = localStorage.getItem(SLACK_TEAM_KEY)
    if (token) {
      setSlackAccessToken(token)
    }
    if (teamInfo) {
      setSlackTeamInfo(JSON.parse(teamInfo))
    }
  }, [])

  // Check for OAuth callback parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get("code")
    const state = urlParams.get("state")

    if (code && state && state.startsWith("aix-")) {
      void handleSlackOAuthCallback(code, state)
    }
  }, [])

  // Create the mutation for Slack OAuth exchange
  const slackExchangeMutation = trpc.slack.exchangeCode.useMutation({
    onSuccess: (result) => {
      // Store access token and team info
      localStorage.setItem(SLACK_TOKEN_KEY, result.accessToken)
      localStorage.setItem(SLACK_TEAM_KEY, JSON.stringify(result.team))

      setSlackAccessToken(result.accessToken)
      setSlackTeamInfo(result.team)

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)

      alert(`Successfully connected to Slack workspace: ${result.team.name}`)
    },
    onError: (error) => {
      console.error("OAuth callback error:", error)
      alert(`Failed to connect to Slack: ${error.message}`)
    },
  })

  const handleSlackOAuthCallback = async (code: string, state: string) => {
    setIsLoading(true)
    try {
      await slackExchangeMutation.mutateAsync({ code, state })
    } catch (error: any) {
      console.error("OAuth callback error:", error)
      alert(`Failed to connect to Slack: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Update integrations list based on Slack connection status
  const integrations = AVAILABLE_INTEGRATIONS.map((integration) => {
    if (integration.id === "slack") {
      return { ...integration, isConnected: !!slackAccessToken }
    }
    return integration
  })

  // tRPC queries for integration data
  const { data: geekbotStandups, isLoading: geekbotLoading } = trpc.integrations.getGeekbotStandups.useQuery()
  const { data: geekbotReports } = trpc.integrations.getGeekbotReports.useQuery({})

  // Linear queries
  const { data: linearMembers, isLoading: linearMembersLoading } = trpc.integrations.getLinearMembers.useQuery()
  const { data: linearTeams, isLoading: linearTeamsLoading } = trpc.integrations.getLinearTeams.useQuery()
  const { data: linearTeamTasks, isLoading: linearTasksLoading } = trpc.integrations.getLinearTeamTasks.useQuery(
    { teamId: selectedTeam },
    { enabled: !!selectedTeam },
  )

  // Slack queries - only run if we have access token
  const { data: slackChannels } = trpc.slack.getChannels.useQuery(
    { accessToken: slackAccessToken },
    { enabled: !!slackAccessToken },
  )
  const { data: slackUsers } = trpc.slack.getUsers.useQuery(
    { accessToken: slackAccessToken },
    { enabled: !!slackAccessToken },
  )
  const { data: slackWorkspaceInfo } = trpc.slack.getWorkspaceInfo.useQuery(
    { accessToken: slackAccessToken },
    { enabled: !!slackAccessToken },
  )

  // Get Slack OAuth URL
  const { data: slackAuthData } = trpc.slack.getAuthUrl.useQuery({})

  // Send test message mutation
  const sendTestMessage = trpc.slack.sendMessage.useMutation({
    onSuccess: () => {
      alert("Test message sent successfully!")
    },
    onError: (error) => {
      alert(`Failed to send message: ${error.message}`)
    },
  })

  // Send bulk notification mutation
  const sendBulkNotification = trpc.slack.sendBulkNotification.useMutation({
    onSuccess: (result) => {
      alert(`Bulk notification sent! Success: ${result.successful}/${result.totalTargets}`)
    },
    onError: (error) => {
      alert(`Failed to send bulk notification: ${error.message}`)
    },
  })

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch =
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = categoryFilter === "all" || integration.category.toLowerCase() === categoryFilter
    return matchesSearch && matchesCategory
  })

  const selectedIntegrationData = integrations.find((i) => i.id === selectedIntegration)

  const categories = ["all", ...Array.from(new Set(integrations.map((i) => i.category.toLowerCase())))]

  // Handler functions for Slack actions
  const handleTestSlackMessage = async () => {
    if (!slackAccessToken) {
      alert("Please connect to Slack first")
      return
    }

    const channel = "#general" // Default channel
    try {
      await sendTestMessage.mutateAsync({
        accessToken: slackAccessToken,
        channel,
        text: "Hello from AIX Command Center! This is a test message.",
        blocks: [
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: "*Hello from AIX Command Center!* 👋\n\nThis is a test message to verify the integration is working properly.",
            },
          },
        ],
      })
    } catch (error: any) {
      console.error("Failed to send test message:", error)
    }
  }

  const handleBulkNotification = async () => {
    if (!slackAccessToken) {
      alert("Please connect to Slack first")
      return
    }

    try {
      await sendBulkNotification.mutateAsync({
        accessToken: slackAccessToken,
        text: "🚀 AIX Command Center Update: New features have been deployed!",
        targets: [
          { id: "#general", type: "channel" },
          { id: "#updates", type: "channel" },
        ],
      })
    } catch (error: any) {
      console.error("Failed to send bulk notification:", error)
    }
  }

  const renderIntegrationData = () => {
    if (!selectedIntegrationData) return null

    switch (selectedIntegrationData.id) {
      case "geekbot":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Recent Standups</h3>
              {geekbotLoading ? (
                <div className="text-gray-500">Loading standups...</div>
              ) : geekbotStandups && Array.isArray(geekbotStandups) && geekbotStandups.length > 0 ? (
                <div className="space-y-2">
                  {geekbotStandups.slice(0, 5).map((standup: any, index: number) => (
                    <div key={index} className="bg-foregroundx p-3 rounded-lg">
                      <div className="font-medium">{standup.name}</div>
                      <div className="text-sm ">
                        Team: {standup.team?.name || "Unknown"} | Created:{" "}
                        {standup.created_at ? new Date(standup.created_at).toLocaleDateString() : "Unknown"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500">No standups found</div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Recent Reports</h3>
              {geekbotReports ? (
                <div className="space-y-3">
                  {Array.isArray(geekbotReports) && geekbotReports.length > 0 ? (
                    geekbotReports.slice(0, 10).map((report: any, index: number) => (
                      <div key={report.id || index} className="bg-background border border-border p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium ">
                                {report.member?.real_name || report.member?.username || "Unknown User"}
                              </span>
                              <span className="text-sm text-gray-500">
                                {report.created_at ? new Date(report.created_at).toLocaleDateString() : "No date"}
                              </span>
                            </div>
                            <div className="text-sm  mb-2">Standup: {report.standup?.name || "Unknown Standup"}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-400">ID: {report.id}</div>
                            <div className="relative group">
                              <button className="text-gray-400 hover: p-1 rounded" title="View raw JSON data">
                                🔍
                              </button>
                              <div className="absolute top-0 right-0 mt-8 w-96 max-h-96 bg-gray-900 text-gray-100 text-xs p-3 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-auto">
                                <div className="font-mono whitespace-pre-wrap break-words">
                                  {JSON.stringify(report, null, 2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {report.questions && Array.isArray(report.questions) && (
                          <div className="space-y-2">
                            {report.questions.map((q: any, qIndex: number) => (
                              <div key={qIndex} className="bg-foregroundx p-3 rounded">
                                <div className="text-sm font-medium text-primary mb-1">{q.text || "Question"}</div>
                                <div className="text-sm ">{q.answer || "No answer provided"}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {report.answers && Object.keys(report.answers).length > 0 && (
                          <div className="mt-3 space-y-2">
                            {Object.entries(report.answers).map(([key, value]: [string, any], aIndex: number) => (
                              <div key={aIndex} className="bg-foregroundx p-3 rounded">
                                <div className="text-sm font-medium text-primary mb-1">{key}</div>
                                <div className="text-sm ">
                                  {typeof value === "string" ? value : JSON.stringify(value)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500">No reports found</div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500">Loading reports...</div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Integration Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedIntegrationData.features.map((feature, index) => (
                  <div key={index} className="bg-green-50 border border-green-200 p-3 rounded-lg">
                    <div className="text-green-800 font-medium">✓ {feature}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )

      case "slack":
        return (
          <div className="space-y-6">
            {slackAccessToken ? (
              <>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">
                        Connected to {slackTeamInfo?.name || "Slack"}
                      </h3>
                      <p className="text-green-600">OAuth integration active</p>
                    </div>
                    <button
                      onClick={() => {
                        localStorage.removeItem(SLACK_TOKEN_KEY)
                        localStorage.removeItem(SLACK_TEAM_KEY)
                        setSlackAccessToken(null)
                        setSlackTeamInfo(null)
                      }}
                      className="text-destructive hover:text-destructive text-sm"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Channels ({slackChannels?.length || 0})</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {slackChannels?.slice(0, 10).map((channel: any) => (
                        <div key={channel.id} className="bg-foregroundx p-3 rounded-lg">
                          <div className="font-medium">#{channel.name}</div>
                          {channel.topic && <div className="text-sm ">{channel.topic}</div>}
                          <div className="text-xs text-gray-500">{channel.num_members} members</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Team Members ({slackUsers?.length || 0})</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {slackUsers?.slice(0, 10).map((user: any) => (
                        <div key={user.id} className="bg-foregroundx p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            {user.profile?.image_32 && (
                              <img src={user.profile.image_32} alt={user.name} className="w-6 h-6 rounded-full" />
                            )}
                            <div>
                              <div className="font-medium">{user.profile?.display_name || user.name}</div>
                              <div className="text-sm ">{user.profile?.title}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Quick Actions</h3>
                  <div className="flex gap-4">
                    <button
                      onClick={handleTestSlackMessage}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                      disabled={sendTestMessage.isLoading}
                    >
                      {sendTestMessage.isLoading ? "Sending..." : "Send Test Message"}
                    </button>
                    <button
                      onClick={handleBulkNotification}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                      disabled={sendBulkNotification.isLoading}
                    >
                      {sendBulkNotification.isLoading ? "Sending..." : "Send Bulk Notification"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-lg font-semibold mb-2">Connect to Slack</h3>
                <p className=" mb-6">Connect your Slack workspace to enable team notifications and integrations</p>
                {slackAuthData?.authUrl && (
                  <a
                    href={slackAuthData.authUrl}
                    className="bg-slack text-white px-6 py-3 rounded-lg hover:bg-slack-dark transition-colors inline-flex items-center gap-2"
                  >
                    <span>Connect to Slack</span>
                  </a>
                )}
              </div>
            )}
          </div>
        )

      case "linear":
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-800">Connected to Linear</h3>
                  <p className="text-green-600">API integration active</p>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setSelectedTeam(null)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    !selectedTeam
                      ? "border-blue-500 "
                      : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setSelectedTeam(null)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    selectedTeam
                      ? "border-blue-500 "
                      : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300"
                  }`}
                >
                  Teams
                </button>
              </nav>
            </div>

            {!selectedTeam ? (
              // Overview Tab
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Team Members */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Team Members ({linearMembers?.length || 0})</h3>
                  {linearMembersLoading ? (
                    <div className="text-gray-500">Loading members...</div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {linearMembers?.slice(0, 10).map((member: any) => (
                        <div key={member.id} className="bg-foregroundx p-3 rounded-lg">
                          <div className="flex items-center gap-3">
                            {member.avatarUrl && (
                              <img src={member.avatarUrl} alt={member.name} className="w-8 h-8 rounded-full" />
                            )}
                            <div>
                              <div className="font-medium">{member.displayName || member.name}</div>
                              <div className="text-sm ">{member.email}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Teams/Workspaces */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Teams ({linearTeams?.length || 0})</h3>
                  {linearTeamsLoading ? (
                    <div className="text-gray-500">Loading teams...</div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {linearTeams?.map((team: any) => (
                        <div key={team.id} className="bg-foregroundx p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            {team.icon && <span className="text-lg">{team.icon}</span>}
                            <div>
                              <div className="font-medium">{team.name}</div>
                              <div className="text-sm ">
                                {team.issueCount} issues | {team.private ? "Private" : "Public"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Team Tasks View
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <button onClick={() => setSelectedTeam(null)} className=" hover:text-blue-700 text-sm">
                    ← Back to Teams
                  </button>
                  <h3 className="text-lg font-semibold">Tasks ({linearTeamTasks?.length || 0})</h3>
                </div>

                {linearTasksLoading ? (
                  <div className="text-gray-500">Loading tasks...</div>
                ) : (
                  <div className="space-y-3">
                    {linearTeamTasks?.map((task: any) => (
                      <div key={task.id} className="bg-background border border-border p-4 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{task.title}</h4>
                              <span
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: task.state?.color ? `${task.state.color}20` : "#f3f4f6",
                                  color: task.state?.color || "#6b7280",
                                }}
                              >
                                {task.state?.name}
                              </span>
                            </div>
                            {task.description && <p className="text-sm  mb-2 line-clamp-2">{task.description}</p>}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              {task.assignee && <span>Assigned to: {task.assignee.name}</span>}
                              {task.priority && <span>Priority: {task.priority}</span>}
                              {task.estimate && <span>Estimate: {task.estimate}pts</span>}
                              {task.team && <span>Team: {task.team.name}</span>}
                            </div>
                          </div>
                        </div>
                        {task.labels?.nodes?.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {task.labels.nodes.map((label: any) => (
                              <span
                                key={label.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs"
                                style={{
                                  backgroundColor: `${label.color}20`,
                                  color: label.color,
                                }}
                              >
                                {label.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Teams Grid - shown when not viewing specific team tasks */}
            {!selectedTeam && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Browse Teams</h3>
                {linearTeamsLoading ? (
                  <div className="text-gray-500">Loading teams...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {linearTeams?.map((team: any) => (
                      <div
                        key={team.id}
                        className="bg-background border border-border p-4 rounded-lg cursor-pointer hover:border-gray-300 transition-colors"
                        onClick={() => setSelectedTeam(team.id)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {team.icon && <span className="text-lg">{team.icon}</span>}
                          <h4 className="font-medium">{team.name}</h4>
                        </div>
                        {team.description && <p className="text-sm  mb-3 line-clamp-2">{team.description}</p>}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">{team.members?.nodes?.length || 0} members</span>
                          <span className="">{team.issueCount} issues</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          {team.private ? "🔒 Private Team" : "🌐 Public Team"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )

      default:
        return (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">{selectedIntegrationData.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{selectedIntegrationData.name}</h3>
              <p className=" mb-6">{selectedIntegrationData.description}</p>
              <div className="text-orange-600 font-medium">Coming Soon</div>
            </div>
          </div>
        )
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div>Processing OAuth callback...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-foregroundx">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold  mb-2">Integrations</h1>
          <p className="">Connect and manage your third-party integrations to streamline your workflow</p>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-80 bg-background rounded-lg shadow-sm border border-border p-6 h-fit">
            {/* Search */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Integration List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium  mb-3">Available Integrations</h3>
              {filteredIntegrations.map((integration) => (
                <div
                  key={integration.id}
                  onClick={() => setSelectedIntegration(integration.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                    selectedIntegration === integration.id
                      ? "bg-blue-50 border-blue-200"
                      : "bg-background border-border "
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{integration.icon}</span>
                      <span className="font-medium text-sm">{integration.name}</span>
                    </div>
                    <div
                      className={`w-2 h-2 rounded-full ${integration.isConnected ? "bg-green-500" : "bg-foreground"}`}
                    />
                  </div>
                  <p className="text-xs ">{integration.description}</p>
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        integration.isConnected ? "bg-green-100 text-green-800" : "bg-foregroundx text-gray-800"
                      }`}
                    >
                      {integration.isConnected ? "Connected" : "Available"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 bg-background rounded-lg shadow-sm border border-border p-8">
            {selectedIntegrationData && (
              <>
                {/* Integration Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{selectedIntegrationData.icon}</div>
                    <div>
                      <h2 className="text-2xl font-bold ">{selectedIntegrationData.name}</h2>
                      <p className="">{selectedIntegrationData.description}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            selectedIntegrationData.isConnected
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {selectedIntegrationData.isConnected ? "✓ Connected" : "⚠ Not Connected"}
                        </span>
                        <span className="text-sm text-gray-500">Provider: {selectedIntegrationData.provider}</span>
                      </div>
                    </div>
                  </div>
                  <a
                    href={selectedIntegrationData.documentation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" hover:text-blue-700 text-sm"
                  >
                    View Docs →
                  </a>
                </div>

                {/* Integration Data */}
                {renderIntegrationData()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default IntegrationsScreen
